import React, {useEffect, useState} from 'react';
import {Badge, Box, makeStyles, Tab, Tabs, Typography} from '@material-ui/core';
import PromotionCard from 'Components/PromotionCard';
import clsx from 'clsx';
import {BP_LARGE, BP_MEDIUM, BP_XLARGE} from "Theme/breakpoints";
import useModal from "Hooks/useModal";
import usePromotions, {usePromotionsProps} from "Hooks/api/usePromotions";
import {BINGO_ENABLED, IS_LOGGED_IN, IS_STAGING} from "Setup/config";
import {MQMinLarge, MQMinMedium, MQMinXLarge} from "Helpers/mediaQueries";
import {PROMO_PREFIXES} from "Constants";

const usePromotionPageStyles = makeStyles(theme => {
    const FilterTabSettings = theme.overrides.filterTab;

    return {
        root: {
            '&.row': {
                maxWidth: '88rem',
            },
            '&.column ': {
                [theme.breakpoints.down(BP_MEDIUM)]: {
                    padding: '0'
                },
            },
            '& .MuiTypography-h4': {
                [theme.breakpoints.down(BP_MEDIUM)]: {
                    paddingLeft: theme.spacing(3),
                },
            }
        },
        promotionCardsContainer: {
            display: 'flex',
            flexWrap: 'wrap',
            '& > div': {
                flex: '0 1 25%',
                [theme.breakpoints.down(BP_XLARGE)]: {
                    flex: '0 1 33%'
                },
                [theme.breakpoints.down(BP_LARGE)]: {
                    flex: '0 1 50%'
                },
                [theme.breakpoints.down(BP_MEDIUM)]: {
                    flex: '0 1 100%'
                },
            }
        },
        cardWrapper: {
            padding: theme.spacing(2, 0),

            [theme.breakpoints.up(BP_MEDIUM)]: {
                padding: theme.spacing(2, 2),
            }
        },
        filter: {
            borderTop: FilterTabSettings.dividerColor,
            borderBottom: FilterTabSettings.dividerColor,
            marginBottom: theme.spacing(2),
            '& .MuiBadge-root': {
                position: "unset",
                '& .MuiBadge-badge': {
                    top: 4,
                    right: 4
                }
            },

            '& .MuiTab-root': {
                overflow: "visible",
                border: FilterTabSettings.border,
                borderRadius: FilterTabSettings.borderRadius,
                textTransform: 'unset',
                margin: FilterTabSettings.margin,
                minWidth: 80,
                minHeight: 35,
                padding: theme.spacing(0, 1.5),
                background: FilterTabSettings.background,
                color: FilterTabSettings.color,
                '&.Mui-selected': {
                    backgroundColor: FilterTabSettings.activeBackground,
                    color: FilterTabSettings.activeColor,
                },
                '&.Mui-disabled': {
                    // opacity: 0.3,
                    display: 'none',
                },
            },
            '& .MuiSvgIcon-fontSizeSmall': {
                fontSize: '2.5rem',
                fill: 'white',
            },
            '& .MuiTabScrollButton-root:first-child': {
                left: 0,
                borderRadius: '0px 100px 100px 0px',
            },
            '& .MuiTabScrollButton-root': {
                right: 0,
                position: 'absolute',
                width: 40,
                height: 80,
                background: theme.palette.layout.xdark,
                borderRadius: '100px 0px 0px 100px',
                zIndex: 1,
                [theme.breakpoints.up(BP_LARGE)]: {
                    display: 'none'
                },
            },
        }
    }
});

const SkeletonPromoCards = (props) => {
    const classes = usePromotionPageStyles();
    let cards = [];
    if (props.count > 0) {
        for (let i = 0; i < props.count; i++) {
            cards.push(
                <MQMinMedium ignore={i < 1}>
                    <MQMinLarge ignore={i < 2}>
                        <MQMinXLarge ignore={i < 3}>
                            <PromotionCard skeleton={true}/>
                        </MQMinXLarge>
                    </MQMinLarge>
                </MQMinMedium>
            );
        }

        return (
            <>
                {cards.map((card, key) => {
                    return <div className={clsx(classes.cardWrapper, 'columns')} key={`skeleton-card-${key}`}>{card}</div>
                })}
            </>
        );
    }

    return null;
};

SkeletonPromoCards.defaultProps = {
    count: 4
}

function TabPanel(props) {
    const {children, value, index} = props;
    return (
        <div>
            {value === index && (
                <>{children}</>
            )}
        </div>
    );
}

export default function Promos() {
    const classes = usePromotionPageStyles();
    const {openModal} = useModal();
    const {_list} = usePromotions();
    const {listPromotions} = _list();

    const {unclaimedPromotions, unclaimedPromotionsLoaded} = usePromotionsProps();

    const [activeTab, setActiveTab] = useState(0);
    const handleChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    useEffect(() => {
        if (!IS_LOGGED_IN && !unclaimedPromotionsLoaded) {
            listPromotions();
        }
    }, [unclaimedPromotionsLoaded]);

    // const allPromotions = unclaimedPromotionsLoaded ? unclaimedPromotions.filter(p => !(p.internalName.includes(PROMO_PREFIXES.NOTIFICATION)) && (p.canBeClaimed || (!p.canBeClaimed && p.bonusType !== "CashbackOnNetLosses"))) : [];
    const claimablePromotions = unclaimedPromotionsLoaded ? unclaimedPromotions.filter(p => p.canBeClaimed && !(p.internalName.includes(PROMO_PREFIXES.NOTIFICATION))) : [];
    const bingoPromotions = unclaimedPromotionsLoaded ? unclaimedPromotions.filter(p => p.internalName.includes('_BINGO') && !(p.internalName.includes(PROMO_PREFIXES.NOTIFICATION))) : [];
    const casinoPromotions = unclaimedPromotionsLoaded ? unclaimedPromotions.filter(p => p.internalName.includes('_CASINO') && !(p.internalName.includes(PROMO_PREFIXES.NOTIFICATION))) : [];
    const allPromotions = unclaimedPromotionsLoaded ? [...claimablePromotions, ...casinoPromotions, ...bingoPromotions] : [];

    const PROMOS_NAVIGATIONS_ITEMS = [
        {name: "All", filter: allPromotions, showBadge: false},
        {name: "Claim Now", filter: claimablePromotions, showBadge: true},
        {name: "Casino", filter: casinoPromotions, showBadge: false},
        BINGO_ENABLED && {name: "Bingo", filter: bingoPromotions, showBadge: false},
    ].filter(Boolean);

    return (
        <section className={clsx("column", "row", classes.root)}>
            <Box py={4}>
                <Box textAlign="center" mb={4}>
                    <Typography component="h2" variant="h4">Promotions</Typography>
                </Box>
                <Tabs
                    className={classes.filter}
                    value={activeTab}
                    onChange={handleChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    TabIndicatorProps={{
                        style: {display: 'none'}
                    }}
                >
                    {PROMOS_NAVIGATIONS_ITEMS.map((item, key) => (
                        <Tab key={`navigation-item-${key}`} disabled={item.filter.length === 0}
                             label={<Badge badgeContent={!item.showBadge ? 0 : item.filter.length} color="primary">
                                 {item.name}
                             </Badge>}/>
                    ))}
                </Tabs>
                {PROMOS_NAVIGATIONS_ITEMS.map((item, key) => {
                        return (
                            <TabPanel value={activeTab} index={key} key={`panel-${key}`}>
                                {/*{(!unclaimedPromotionsLoaded || allPromotions.length > 0) &&*/}
                                {/*    <Box className="columns">*/}
                                {/*        <Typography variant="h5">{item.name}</Typography>*/}
                                {/*    </Box>*/}
                                {/*}*/}
                                <Box pb={3} className={clsx(classes.promotionCardsContainer)}>
                                    {!unclaimedPromotionsLoaded ? <SkeletonPromoCards/> :
                                        <>
                                            {item.filter && item.filter.map((promo, key) => {
                                                return (
                                                    <div className={clsx(classes.cardWrapper, 'columns')} key={`promo-${key}`}>
                                                        <PromotionCard
                                                            promoId={promo.id}
                                                            image={promo.images.default}
                                                            title={promo.displayName}
                                                            description={promo.description}
                                                            bonusType={promo.bonusType}
                                                            openModal={() => openModal('Promotion', {promoId: promo.id})}
                                                        />
                                                    </div>
                                                )
                                            })}
                                        </>
                                    }
                                </Box>
                            </TabPanel>)
                    }
                )}
            </Box>
        </section>
    );
}
